/**
 * Layer sharing helpers (pure, unit tested): who can see the sheet (recipients who can't are greyed
 * out), and conversion between the share list and the share sheet's chip/people state.
 */
import { isVisibleTo, matchesOwnAudience, SECTIONS, type Role, type Section } from '../../shared/audience';
import type { LayerShareTarget } from '../../shared/dto/annotations';

/** Can members of `section` see a sheet with this audience mask (ignoring editor privileges)? */
export function sectionCanSee(mask: number, section: Section): boolean {
  return matchesOwnAudience(mask, section);
}

/** Can this member see the sheet? Editors/admins always can. */
export function memberCanSee(mask: number, member: { role: Role; section: Section | null }): boolean {
  return isVisibleTo(mask, member);
}

export interface ShareDraft {
  all: boolean;
  sections: Section[];
  users: string[];
}

export function draftFromShares(shares: readonly LayerShareTarget[]): ShareDraft {
  const d: ShareDraft = { all: false, sections: [], users: [] };
  for (const s of shares) {
    if (s.type === 'all') d.all = true;
    else if (s.type === 'section' && !d.sections.includes(s.section)) d.sections.push(s.section);
    else if (s.type === 'user' && !d.users.includes(s.userId)) d.users.push(s.userId);
  }
  d.sections = SECTIONS.filter((s) => d.sections.includes(s));
  return d;
}

/** "Allur kórinn" already includes every section, so sections are dropped when it is on. */
export function sharesFromDraft(d: ShareDraft): LayerShareTarget[] {
  const out: LayerShareTarget[] = [];
  if (d.all) out.push({ type: 'all' });
  else for (const s of SECTIONS) if (d.sections.includes(s)) out.push({ type: 'section', section: s });
  for (const u of [...new Set(d.users)]) out.push({ type: 'user', userId: u });
  return out;
}

export function isShared(shares: readonly LayerShareTarget[]): boolean {
  return shares.length > 0;
}
